import { RateSource } from "@redotech/money/rate-source";
import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import PackageIcon from "@redotech/redo-icons/package.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { TextAndSupportingText } from "@redotech/redo-web/text-and-supporting-text";
import { isDef } from "@redotech/util/checks";
import { memo, ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDeliveryText,
  getLabelText,
} from "../../common/shipment-rate-selection-dropdown";
import { OutboundRateOption } from "../../common/util";
import {
  getMarginBreakdown,
  InternationalFees,
} from "../../utils/get-margin-breakdown";

export const MarginBreakdownModal = memo(function MarginBreakdownModal({
  open,
  onClose,
  fulfillmentGroup,
  selectedRate,
}: {
  open: boolean;
  onClose: () => void;
  fulfillmentGroup: FulfillmentGroupData;
  selectedRate: OutboundRateOption;
}) {
  const team = useRequiredContext(TeamContext);

  const navigate = useNavigate();

  const marginBreakdown = useMemo(
    () =>
      getMarginBreakdown(
        fulfillmentGroup,
        selectedRate.rate || 0,
        selectedRate.landedCost,
      ),
    [fulfillmentGroup, selectedRate],
  );

  const { customerInternationalFees, merchantInternationalFees } =
    marginBreakdown;

  const revenueCurrency = fulfillmentGroup.totalShippingCost.currency;

  const mixedCurrency =
    (!!selectedRate.currency && selectedRate.currency !== revenueCurrency) ||
    (!!merchantInternationalFees &&
      merchantInternationalFees.currency !== revenueCurrency);

  return (
    <RedoModal
      buttonPlacement="tight"
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Improve margin",
        onClick: () => {
          navigate(`/stores/${team._id}/conversion/checkouts`);
        },
      }}
      subtitle="See what you're paying vs. what customers are paying in shipping methods."
      title="Margin breakdown"
      TitleIcon={PackageIcon}
    >
      <Flex dir="column" gap="none">
        {marginBreakdown.fulfillmentOrdersBreakdown.map((fulfillmentOrder) => (
          <>
            <MarginBreakdownLineItem>
              <TextAndSupportingText
                mainTextSize="xs"
                supportingText="Paid by the customer"
                supportingTextSize="xxs"
                text="Shipping paid"
              />
              <Text fontSize="xs" textColor="primary">
                <MoneyText
                  source={RateSource.Stripe}
                  value={toMoney({
                    amount: fulfillmentOrder.shippingPaid,
                    currency: fulfillmentGroup.totalShippingCost.currency,
                  })}
                />
              </Text>
            </MarginBreakdownLineItem>
            <MarginBreakdownLineItem indented>
              <TextAndSupportingText
                mainTextSize="xxs"
                supportingText="Paid by the customer"
                supportingTextSize="xxs"
                text="Quoted label cost"
              />
              <Text fontSize="xxs" textColor="primary">
                <MoneyText
                  source={RateSource.Stripe}
                  value={toMoney({
                    amount: fulfillmentOrder.labelQuotedCost,
                    currency: fulfillmentGroup.totalShippingCost.currency,
                  })}
                />
              </Text>
            </MarginBreakdownLineItem>
            <MarginBreakdownLineItem indented>
              <TextAndSupportingText
                mainTextSize="xxs"
                supportingText="Paid by the customer"
                supportingTextSize="xxs"
                text="Percentage markup"
              />
              <TextAndSupportingText
                align="flex-end"
                mainTextSize="xxs"
                supportingText={moneyFormat({
                  amount: fulfillmentOrder.percentageMarkupCost,
                  currency: fulfillmentGroup.totalShippingCost.currency,
                })}
                supportingTextSize="xxs"
                text={`${fulfillmentOrder.percentageMarkup}%`}
              />
            </MarginBreakdownLineItem>
            <MarginBreakdownLineItem indented>
              <TextAndSupportingText
                mainTextSize="xxs"
                supportingText="Paid by the customer"
                supportingTextSize="xxs"
                text="Fixed markup"
              />
              <Text fontSize="xxs" textColor="primary">
                <MoneyText
                  source={RateSource.Stripe}
                  value={toMoney({
                    amount: fulfillmentOrder.fixedMarkup,
                    currency: fulfillmentGroup.totalShippingCost.currency,
                  })}
                />
              </Text>
            </MarginBreakdownLineItem>
          </>
        ))}
        {customerInternationalFees && (
          <InternationalFeesRows
            currency={revenueCurrency}
            fees={customerInternationalFees}
            label="International fees paid"
            supportingText="Paid by the customer"
          />
        )}
        <MarginBreakdownLineItem>
          <Flex align="center" dir="row" gap="md">
            <ShippingLogo
              carrier={selectedRate.carrier}
              size={LogoSize.MEDIUM}
            />
            <Flex align="flex-start" dir="column" gap="none">
              <Flex dir="row" gap="none">
                <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                  {getLabelText(selectedRate)}
                </Text>
                {isDef(selectedRate.deliveryDays) && (
                  <Text fontSize="xs" textColor="tertiary">
                    {" "}
                    - {getDeliveryText(selectedRate)}
                  </Text>
                )}
              </Flex>
              <Text fontSize="xxs" textColor="tertiary">
                Paid by you
              </Text>
            </Flex>
          </Flex>
          <Text fontSize="xs" textColor="primary">
            <MoneyText
              source={RateSource.Stripe}
              value={toMoney({
                amount: selectedRate.rate ?? 0,
                currency: selectedRate.currency,
              })}
            />
          </Text>
        </MarginBreakdownLineItem>
        {merchantInternationalFees && (
          <InternationalFeesRows
            currency={merchantInternationalFees.currency}
            fees={merchantInternationalFees}
            label="Quoted international fees"
            supportingText="Paid by you"
          />
        )}
        <MarginBreakdownLineItem>
          <Text fontSize="xs" fontWeight="semibold" textColor="primary">
            Total margin
          </Text>
          <Text
            fontSize="xs"
            textColor={marginBreakdown.totalMargin < 0 ? "error" : "success"}
          >
            {/* Margin nets the carrier rate and quoted international fees against
                the (presentment) shipping revenue, so it's only meaningful when
                all three share a currency. Show "—" rather than a mixed figure. */}
            {mixedCurrency ? (
              "—"
            ) : (
              <MoneyText
                source={RateSource.Stripe}
                value={toMoney({
                  amount: marginBreakdown.totalMargin,
                  currency: revenueCurrency,
                })}
              />
            )}
          </Text>
        </MarginBreakdownLineItem>
      </Flex>
    </RedoModal>
  );
});

const InternationalFeesRows = memo(function InternationalFeesRows({
  label,
  supportingText,
  fees,
  currency,
}: {
  label: string;
  supportingText: string;
  fees: InternationalFees;
  currency: string;
}) {
  const components: { label: string; amount: number }[] = [
    { label: "Taxes", amount: fees.taxes },
    { label: "Duties and fees", amount: fees.dutiesAndFees },
  ];

  return (
    <>
      <MarginBreakdownLineItem>
        <TextAndSupportingText
          mainTextSize="xs"
          supportingText={supportingText}
          supportingTextSize="xxs"
          text={label}
        />
        <Text fontSize="xs" textColor="primary">
          <MoneyText
            source={RateSource.Stripe}
            value={toMoney({ amount: fees.total, currency })}
          />
        </Text>
      </MarginBreakdownLineItem>
      {components.map((component) => (
        <MarginBreakdownLineItem indented key={component.label}>
          <TextAndSupportingText
            mainTextSize="xxs"
            supportingText={supportingText}
            supportingTextSize="xxs"
            text={component.label}
          />
          <Text fontSize="xxs" textColor="primary">
            <MoneyText
              source={RateSource.Stripe}
              value={toMoney({ amount: component.amount, currency })}
            />
          </Text>
        </MarginBreakdownLineItem>
      ))}
    </>
  );
});

const MarginBreakdownLineItem = memo(function MarginBreakdownLineItem({
  children,
  indented = false,
}: {
  children: ReactNode;
  indented?: boolean;
}) {
  return (
    <Flex
      align="center"
      borderBottomWidth="1px"
      borderColor="primary"
      borderStyle="solid"
      dir="row"
      justify="space-between"
      pl={indented ? "xl" : undefined}
      py="xl"
    >
      {children}
    </Flex>
  );
});
